"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _reactMintl = require("@godaddy/react-mintl");
var _classnames = _interopRequireDefault(require("classnames"));
var _uxUtils = require("@site/ux-utils");
var _constants = require("./constants");
var _utils = require("@site/utils");
var _PricingMessages = require("./PricingMessages");
// eslint-disable-next-line complexity
const BasePriceBlock = ({
  language = {},
  initialPriceDisplay: initialPriceDisplayOverride,
  initialPriceOverride,
  currentPriceDisplay: currentPriceDisplayOverride,
  currentPriceOverride,
  datacy = 'price-block',
  priceAdditionalText: priceAdditionalTextOverride,
  priceToolTip,
  options = {},
  domain = {},
  product = {},
  messages,
  enableDynamicFontScaling = false,
  plid = 1,
  portalElem
}) => {
  const {
    isMobile
  } = (0, _uxUtils.useDeviceType)();
  const {
    IsFree,
    PriceDisplay,
    Price
  } = domain;
  const {
    PriceInfo = {}
  } = product;
  const market = (0, _utils.getMarket)();

  // Set default values in case they are missing from PriceInfo (for ex. stack bundle)
  const {
    ListPrice,
    ListPriceDisplay = ListPrice,
    CurrentPrice,
    CurrentPriceDisplay = CurrentPrice,
    RenewalPrice = ListPrice,
    RenewalPriceDisplay,
    OneYearSalePrice,
    OneYearSalePriceDisplay,
    AdditionalYearsPrice,
    MultiYearPackagePricing,
    MultiYearPricing,
    PromoRegLengthFlag
  } = PriceInfo;
  messages = {
    ..._constants.baseMessages,
    ...messages
  };
  const {
    classNames = {},
    dueTodayMessage = '',
    isListPricePriority = false,
    isPriceLinePriority = false,
    overrideCurrentPriceToZero = false,
    term = 0,
    showPriceOnly = false,
    showTwoLinePrice,
    isStackBundle,
    displayPercentagePrice = false,
    showListPriceTooltip = false,
    showAdditionalText = false,
    showPriceSavings = false,
    showPremiumRenewalPriceText = false,
    hideListPrice = false,
    showFreeIndicator = false,
    isPromoForNewCustomer = false,
    isAnnualTermMonthlyPrice = false,
    disableAdditionalTextToolTip = false,
    isAiroDomainPlus = false,
    priceAdjustmentInCents = 0,
    currency = 'USD'
  } = options;
  const {
    priceBlock,
    priceLine,
    priceDisclaimer,
    priceInitial,
    priceCurrent,
    qualifiersText,
    premiumRenewalText,
    styleOverrides,
    styleOptions,
    priceDisclaimerWrapper
  } = classNames;
  const {
    mainPriceStyle,
    strikethroughPriceStyle,
    disclaimerStyle
  } = styleOverrides || {};
  const {
    addPriceLineMarginTop = true,
    addPriceBlockMarginRight = true,
    useSmallQualifierText = true,
    isMainPricePrimaryColor = true,
    alignRight = true,
    applyInLineBlock = true
  } = styleOptions || {};
  const isRegPremiumDomain = (0, _utils.isRegPremium)(domain);
  const isAftermarketPremiumDomain = (0, _utils.isAftermarketPremium)(domain);
  const isAuctionDomain = (0, _utils.isAuction)(domain);
  const isAuctionOrAftermarketPremium = isAuctionDomain || isAftermarketPremiumDomain;
  const isAuctionOrPremium = (0, _utils.renderAsPremium)(domain);
  const premiumPrice = isAuctionOrAftermarketPremium ? Price : CurrentPrice;
  const premiumPriceDisplay = isAuctionOrAftermarketPremium ? PriceDisplay : CurrentPriceDisplay;
  const {
    formatMessage
  } = (0, _reactMintl.useIntl)();
  const samePriceEachYear = !isAuctionOrPremium && CurrentPrice && AdditionalYearsPrice && CurrentPrice === AdditionalYearsPrice;
  const isRenewalPriceHigher = RenewalPrice > CurrentPrice;
  const isPriceConsistent = samePriceEachYear && !isRenewalPriceHigher;
  const isFree = showFreeIndicator || IsFree;
  const subTextStyle = isMobile && isRegPremiumDomain ? disclaimerStyle : strikethroughPriceStyle;
  const showMonthlyPriceForATMP = term === 1 && isAnnualTermMonthlyPrice;
  const forceOneYearDisplay = term === 12;
  const showMultiYearTermForATMP = term >= 24 && isAnnualTermMonthlyPrice;
  const currentMultiYearPricing = MultiYearPricing && Array.isArray(MultiYearPricing) ? MultiYearPricing.find(termPricing => termPricing.Term === PromoRegLengthFlag) : {};
  const currentMultiYearPackagePricing = MultiYearPricing && Array.isArray(MultiYearPackagePricing) ? MultiYearPackagePricing[0] : {};
  // Current Price
  const currentPrice = (0, _utils.getCurrentPrice)({
    premiumPrice,
    currentPriceOverride,
    CurrentPrice,
    isFree,
    isAuctionOrAftermarketPremium
  });
  const currentPriceDisplay = (0, _PricingMessages.getFinalCurrentPriceDisplay)({
    premiumPriceDisplay,
    currentPriceDisplayOverride,
    CurrentPriceDisplay,
    messages,
    isFree,
    isAuctionOrAftermarketPremium,
    showTwoLinePrice,
    overrideCurrentPriceToZero,
    isMobile,
    OneYearSalePrice,
    OneYearSalePriceDisplay,
    forceOneYearDisplay,
    samePriceEachYear,
    formatMessage,
    mainPriceStyle,
    showMonthlyPriceForATMP,
    showMultiYearTermForATMP,
    isAiroDomainPlus,
    domainAiroPlusPriceDisplay: currentMultiYearPricing?.FirstYearSalePriceDisplay,
    term,
    PriceInfo
  });

  // List Price
  const listPrice = (0, _utils.getListPrice)({
    initialPriceOverride,
    isAuctionOrAftermarketPremium,
    ListPrice,
    RenewalPrice,
    currentPrice,
    showTwoLinePrice
  });
  const {
    listPriceDisplay,
    listPriceTwoYearTermDisplay
  } = (0, _PricingMessages.getFinalListPriceDisplay)({
    initialPriceDisplayOverride,
    isAuctionOrAftermarketPremium,
    ListPrice,
    currentPrice,
    ListPriceDisplay,
    showTwoLinePrice,
    messages,
    isMobile,
    samePriceEachYear,
    isPriceConsistent,
    formatMessage,
    isAiroDomainPlus,
    domainAiroPlusPriceDisplay: priceAdjustmentInCents ? (0, _utils.formatCurrency)((currentMultiYearPackagePricing?.ListPrice || 0) + priceAdjustmentInCents / 100, market, currency) : currentMultiYearPackagePricing?.ListPriceDisplay
  });

  // Due Today
  const oneYearTermPrice = (0, _utils.getOneYearTermCurrentPrice)({
    OneYearSalePrice,
    currentPrice
  });
  const actualCurrentPrice = forceOneYearDisplay ? oneYearTermPrice : currentPrice;
  const dueTodayPrice = (0, _utils.getDueTodayPrice)({
    forceOneYearDisplay,
    actualCurrentPrice,
    listPrice,
    showTwoLinePrice,
    isAuctionOrAftermarketPremium,
    RenewalPrice
  });
  const dueToday = dueTodayPrice && dueTodayMessage && formatMessage({
    id: dueTodayMessage
  }, {
    price: dueTodayPrice
  });

  // Savings
  const priceSavings = (0, _utils.calculatePriceSavingsDisplay)({
    actualCurrentPrice,
    listPrice,
    percentageSavingsMessage: messages.percentageSavings,
    moneySavingsMessage: messages.moneySavings,
    displayPercentagePrice,
    isAuctionOrAftermarketPremium,
    formatMessage
  });

  // Disclaimer, Qualifier, and Tooltip
  const {
    priceAdditionalText,
    priceToolTipComponent
  } = (0, _PricingMessages.getAdditionalTextAndToolTipComponent)({
    formatMessage,
    priceAdditionalTextOverride,
    priceToolTip,
    options,
    messages,
    domain,
    product,
    samePriceEachYear,
    isPriceConsistent,
    showTwoLinePrice,
    isStackBundle,
    isPromoForNewCustomer,
    disableAdditionalTextToolTip,
    portalElem,
    term
  });
  const formattedPriceAdditionalText = (0, _PricingMessages.getAdditionalPriceText)({
    priceAdditionalText,
    priceDisclaimer,
    styleOverrides,
    isMobile,
    isAuctionOrAftermarketPremium,
    showTwoLinePrice,
    enableDynamicFontScaling,
    isAftermarketPremiumDomain,
    plid
  });
  const premiumRenewalPriceText = (0, _PricingMessages.getPremiumRenewalPriceText)({
    isRegPremiumDomain,
    isAftermarketPremiumDomain,
    messages
  });
  const qualifiersTextId = (0, _utils.getQualifiersTextId)(domain.AuctionTypeId);
  const isMainPriceTooltipVisible = (0, _PricingMessages.isTooltipShownAfterMainPrice)({
    showPriceOnly,
    showTwoLinePrice,
    showListPriceTooltip,
    isMobile,
    isAuctionOrAftermarketPremium
  });
  const isListPriceTooltipVisible = (0, _PricingMessages.isTooltipShownAfterListPrice)({
    isMobile,
    isAuctionOrAftermarketPremium,
    showTwoLinePrice,
    showListPriceTooltip,
    isRegPremiumDomain
  });
  const isAdditionalTextVisible = (0, _PricingMessages.isAdditionalTextShown)({
    showPriceOnly,
    showTwoLinePrice,
    qualifiersTextId,
    showAdditionalText,
    isAuctionOrPremium
  });
  const mainPriceFontSize = mainPriceStyle?.fontSize;
  return /*#__PURE__*/_react.default.createElement(_reactMintl.IntlProvider, {
    locale: market,
    key: market,
    messages: language
  }, /*#__PURE__*/_react.default.createElement("div", {
    "data-cy": datacy,
    "data-testid": datacy,
    className: (0, _classnames.default)({
      'mr-1': addPriceBlockMarginRight,
      'py-2': !isMobile,
      'text-sm-right': alignRight,
      'd-flex': showTwoLinePrice,
      'd-flex align-items-baseline': isAftermarketPremiumDomain
    }, priceBlock)
  }, /*#__PURE__*/_react.default.createElement("div", {
    "data-cy": "price-line-and-savings",
    className: (0, _classnames.default)({
      'd-inline-block': applyInLineBlock,
      'd-flex flex-column-reverse': showTwoLinePrice && isPriceLinePriority,
      'd-flex flex-column': showTwoLinePrice && !isPriceLinePriority
    })
  }, /*#__PURE__*/_react.default.createElement("span", {
    className: (0, _classnames.default)('d-flex', {
      'align-items-end': isMobile
    }),
    "data-cy": "savings-due-tooltip",
    "data-testid": "savings-due-tooltip"
  }, priceSavings && showPriceSavings && /*#__PURE__*/_react.default.createElement("span", {
    className: "ms-1 font-weight-bold savings-off-term",
    "data-cy": "savings-off-term",
    "data-testid": "savings-off-term"
  }, priceSavings), dueToday && /*#__PURE__*/_react.default.createElement("span", {
    "data-cy": "due-today",
    "data-testid": "due-today",
    className: "ms-1 ml-1 font-weight-normal due-today"
  }, dueToday), !isAdditionalTextVisible && showTwoLinePrice && priceToolTipComponent), /*#__PURE__*/_react.default.createElement("div", {
    "data-cy": "price-line",
    "data-testid": "price-line",
    className: (0, _classnames.default)(priceLine, 'd-flex align-items-baseline flex-wrap mr-1', {
      'mt-2': addPriceLineMarginTop
    })
  }, /*#__PURE__*/_react.default.createElement("span", null, /*#__PURE__*/_react.default.createElement("div", {
    className: (0, _classnames.default)('d-flex align-items-baseline', {
      'flex-wrap': !showTwoLinePrice,
      'flex-row-reverse justify-content-end': isListPricePriority,
      'w-100': !isListPricePriority
    })
  }, /*#__PURE__*/_react.default.createElement("span", {
    'data-cy': 'pricing-main-price',
    'data-testid': 'pricing-main-price',
    'key': 'currentPrice',
    'className': (0, _classnames.default)('pricing-main-price text-nowrap m-b-0', priceCurrent, mainPriceFontSize, {
      'text-black': !mainPriceStyle?.color && !(0, _utils.isReseller)(plid),
      'd-flex align-items-center': showTwoLinePrice,
      'd-inline-block': applyInLineBlock,
      ...(!mainPriceFontSize && {
        ms0: isMobile || !showTwoLinePrice && isListPricePriority && listPriceDisplay && !hideListPrice,
        ms3: !isMobile && !showTwoLinePrice,
        ms1: !isMobile && showTwoLinePrice
      }),
      'ml-2': !showTwoLinePrice && !isMobile && isListPricePriority && listPriceDisplay && !hideListPrice,
      'text-primary-o': !mainPriceStyle?.color && !showTwoLinePrice && isMainPricePrimaryColor && (0, _utils.isReseller)(plid),
      'pricing-main-price-dynamic-font-scaling': enableDynamicFontScaling
    }),
    'style': mainPriceStyle
  }, currentPriceDisplay, showTwoLinePrice && priceAdditionalText && isAuctionOrAftermarketPremium && formattedPriceAdditionalText, !isAdditionalTextVisible && isMainPriceTooltipVisible && priceToolTipComponent, !isListPricePriority && showTwoLinePrice && !isAuctionOrAftermarketPremium && !forceOneYearDisplay && /*#__PURE__*/_react.default.createElement("div", {
    style: {
      height: '2rem'
    },
    className: "bd-l-2 bd-gray w-0 mx-2"
  })), listPriceDisplay && !hideListPrice && /*#__PURE__*/_react.default.createElement("span", {
    'data-cy': 'pricing-strikethrough-price-wrapper',
    'data-testid': 'pricing-strikethrough-price-wrapper',
    'key': 'initialPriceDisplay',
    'className': (0, _classnames.default)({
      'ms0': !isMobile,
      'ml-2': !showTwoLinePrice && !isMobile && !isListPricePriority,
      'd-flex': showTwoLinePrice,
      'text-muted': !strikethroughPriceStyle?.color && !showTwoLinePrice && (0, _utils.isReseller)(plid),
      'text-black': !strikethroughPriceStyle?.color && !(0, _utils.isReseller)(plid),
      'sub-price': isMobile && !showTwoLinePrice,
      'text-nowrap small mr-2': isMobile,
      'ml-1': isMobile && !isListPricePriority
    }, priceInitial)
  }, showTwoLinePrice && !forceOneYearDisplay && !isAuctionOrAftermarketPremium && listPriceTwoYearTermDisplay, (!showTwoLinePrice || forceOneYearDisplay) && /*#__PURE__*/_react.default.createElement("del", {
    "data-cy": "pricing-strikethrough-price",
    "data-testid": "pricing-strikethrough-price",
    className: (0, _classnames.default)('pricing-strikethrough-price', {
      'pricing-strikethrough-price-dynamic-font-scaling': enableDynamicFontScaling
    }),
    style: strikethroughPriceStyle || {}
  }, listPriceDisplay), !isAdditionalTextVisible && isListPriceTooltipVisible && priceToolTipComponent, showTwoLinePrice && isListPricePriority && !forceOneYearDisplay && !isAuctionOrAftermarketPremium && /*#__PURE__*/_react.default.createElement("div", {
    style: {
      height: '2rem'
    },
    className: "bd-l-2 bd-gray w-0 mx-2"
  })))), premiumRenewalPriceText && RenewalPrice && showPremiumRenewalPriceText && /*#__PURE__*/_react.default.createElement("div", {
    "data-cy": "premium-renewal-price",
    "data-testid": "premium-renewal-price",
    className: (0, _classnames.default)({
      'ms-2': isRegPremiumDomain,
      'ml-2': isAuctionOrPremium && !isMobile,
      'text-black': !subTextStyle?.color && !(0, _utils.isReseller)(plid),
      'text-muted': !subTextStyle?.color && (0, _utils.isReseller)(plid)
    }, 'd-inline text-nowrap m-b-0 sub-price small mt-2', premiumRenewalText),
    style: subTextStyle
  }, /*#__PURE__*/_react.default.createElement(_reactMintl.FormattedMessage, {
    id: premiumRenewalPriceText,
    values: {
      yearlyPrice: RenewalPriceDisplay
    }
  })))), qualifiersTextId && /*#__PURE__*/_react.default.createElement("div", {
    className: (0, _classnames.default)('align-self-end pl-1', {
      'ms-2': useSmallQualifierText
    }, qualifiersText),
    "data-cy": `${datacy}-qualifiers-text`,
    "data-testid": `${datacy}-qualifiers-text`,
    style: strikethroughPriceStyle
  }, /*#__PURE__*/_react.default.createElement(_reactMintl.FormattedMessage, {
    id: qualifiersTextId
  })), priceAdditionalText && isAdditionalTextVisible && (isMobile ? /*#__PURE__*/_react.default.createElement("div", {
    className: "d-flex"
  }, formattedPriceAdditionalText, !showTwoLinePrice && priceToolTipComponent) : /*#__PURE__*/_react.default.createElement("div", {
    className: (0, _classnames.default)(`d-flex align-items-start ${priceDisclaimerWrapper}`, {
      'justify-content-end': !priceDisclaimerWrapper
    })
  }, formattedPriceAdditionalText, !showTwoLinePrice && priceToolTipComponent))));
};
BasePriceBlock.propTypes = {
  language: _propTypes.default.object.isRequired,
  initialPriceOverride: _propTypes.default.number,
  currentPriceOverride: _propTypes.default.number,
  initialPriceDisplay: _propTypes.default.string,
  currentPriceDisplay: _propTypes.default.string,
  priceAdditionalText: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]),
  datacy: _propTypes.default.string,
  priceToolTip: _propTypes.default.element,
  domain: _propTypes.default.object,
  product: _propTypes.default.object,
  options: _propTypes.default.object,
  messages: _propTypes.default.object,
  enableDynamicFontScaling: _propTypes.default.bool,
  plid: _propTypes.default.oneOfType([_propTypes.default.number, _propTypes.default.string]),
  portalElem: _propTypes.default.object
};
var _default = exports.default = BasePriceBlock;